import {useMemo, useRef, createRef} from 'react';

import type {User} from 'nutshell-core/users/users-types';

import type {Tab} from 'shells/control-bar/page-control-bar';
import {getWidgetIconForType} from 'shells/widgets/helpers';

import type {Lead} from 'nutshell-graphql-types';

import {MINI_HEADER_HEIGHT_WITHOUT_FORM} from '../../../core-entity/mini-header/mini-header';
import {getWidgetComponents} from '../../../core-entity/util/widget-components';
import {TimelineWidget} from '../../../core-entity/widgets/timeline-widget';
import {getWidgetTabCount} from '../../../core-entity/workspace/helpers';
import {WorkspaceSection as EntityWorkspaceSection} from '../../../core-entity/workspace/workspace-section';

import {getWidgetFallbackLabel, useGetWidgetCounts} from './helpers';

const TIMELINE_WIDGET = {
    __typename: 'Widget',
    id: 'timeline',
    type: 'TIMELINE',
    isHidden: false,
    location: 'WORKSPACE',
    position: 0,
    name: 'Timeline',
    isCollapsedForUser: false,
} as const;

type Props = {
    lead: Lead;
    scrollContainerRef: HTMLDivElement | null | undefined;
    user: User;
    isSidebarCollapsed: boolean;
    isWorkspaceSidebarCollapsed: boolean;
    onIsWorkspaceSidebarCollapsedChange: (isCollapsed: boolean) => void;
    isLeadRecapFeatureEnabled: boolean;
    hasFullHeightSidebar?: boolean;
};

export function WorkspaceSection(props: Props) {
    const {
        scrollContainerRef,
        lead,
        user,
        isSidebarCollapsed,
        isWorkspaceSidebarCollapsed,
        onIsWorkspaceSidebarCollapsedChange,
        isLeadRecapFeatureEnabled,
        hasFullHeightSidebar,
    } = props;
    const workspaceSectionRef = useRef<HTMLDivElement>(null);

    const widgetCounts = useGetWidgetCounts(lead.id);

    const workspaceWidgets = useMemo(() => {
        return lead.stagesetForWidgets?.workspaceWidgets ?? [];
    }, [lead.stagesetForWidgets]);

    const visibleWidgetObjects = useMemo(() => {
        return workspaceWidgets
            .filter((widget) => !widget.isHidden)
            .filter((widget) => !(widget.type === 'SALES_PROCESS' && !lead.stageset))
            .filter((widget) => !(widget.type === 'AI_SUMMARY' && !isLeadRecapFeatureEnabled));
    }, [lead.stageset, isLeadRecapFeatureEnabled, workspaceWidgets]);

    const dynamicWorkspaceWidgetComponents = getWidgetComponents({
        mode: 'lead',
        lead,
        user,
        widgets: visibleWidgetObjects,
    });

    const displayedWidgetComponents = useMemo(() => {
        return [
            ...dynamicWorkspaceWidgetComponents,
            <TimelineWidget
                key='timeline'
                entityId={lead.id}
                entityName='Leads'
                scrollContainerRef={scrollContainerRef}
                hideCreator={true}
                useContainerScroll={true}
            />,
        ];
    }, [dynamicWorkspaceWidgetComponents, lead, scrollContainerRef]);

    const tabOptions: Array<Tab> = useMemo(() => {
        return [...visibleWidgetObjects, TIMELINE_WIDGET].map((widget) => ({
            id: widget.id,
            label: getWidgetFallbackLabel(widget.type, widget.name),
            icon: getWidgetIconForType(widget.type),
            count: getWidgetTabCount(widget.type, widgetCounts),
            hideTooltip: widget.type !== 'CUSTOM_CUSTOM_FIELDS' && !isSidebarCollapsed,
        }));
    }, [isSidebarCollapsed, visibleWidgetObjects, widgetCounts]);

    const tabRefsMap = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});
    const tabRefs = useMemo(() => {
        return tabOptions.reduce<Record<string, React.RefObject<HTMLDivElement>>>((map, w) => {
            const existing = tabRefsMap.current[w.id];
            if (existing) {
                map[w.id] = existing;
            } else {
                const refObj = createRef<HTMLDivElement>();
                tabRefsMap.current[w.id] = refObj;
                map[w.id] = refObj;
            }

            return map;
        }, {});
    }, [tabOptions]);

    return (
        <EntityWorkspaceSection
            tabs={tabOptions}
            widgets={displayedWidgetComponents}
            isCollapsed={isWorkspaceSidebarCollapsed}
            onIsCollapsedChange={onIsWorkspaceSidebarCollapsedChange}
            workspaceSectionRef={workspaceSectionRef}
            scrollContainerRef={scrollContainerRef}
            tabRefs={tabRefs}
            stickyHeaderOffset={MINI_HEADER_HEIGHT_WITHOUT_FORM}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );
}
